import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from "react-google-recaptcha-v3";

import Layout from "../components/Layout/Layout";
import SecondPageTopSection from "../components/TopSection/SecondPageTopSection";
import { BusinessService } from "../services/business-service";
import { StoreService } from "../services/store-service";
import { getCategories } from "../services/server-actions";

import BgImage from "../assets/Bg.webp";
import DefaultLogo from "../assets/images/logo.png";
import { AppConsts } from "../core/AppConsts";

const MobileOtpPage: React.FC = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [code, setCode] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [resendLoading, setResendLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { mobile } = location.state || {};

  const businessService = new BusinessService();
  const businessProfile = StoreService.GetData("business-profile");
  const logo = businessProfile?.logo || DefaultLogo;
  const title = businessProfile?.name || "سالن زیبایی";
  const description = businessProfile?.slogan || "لاین زیبایی";

  const application = StoreService.GetData("user-session")?.application || {};
  const [timeLeft, setTimeLeft] = useState<number>(
    application.twoFactorCodeExpireSeconds || 120
  );

  useEffect(() => {
    if (timeLeft === 0) return;
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => prevTime - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes
      .toString()
      .padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value);
  };

  const handleResendCode = async () => {
    if (resendLoading) return;

    try {
      setResendLoading(true);
      await businessService.sendPasswordlessLoginCode(mobile);

      // پاک کردن مقدار ورودی کد تا کاربر مجبور شود کد جدید را وارد کند
      setCode("");

      // نمایش پیام موفقیت‌آمیز
      toast.success("کد تایید جدید ارسال شد.", { theme: "colored" });

      // ریست کردن تایمر برای ۲ دقیقه‌ی جدید
      setTimeLeft(120);
    } catch (error) {
      toast.error("خطایی در ارسال مجدد کد رخ داد.", { theme: "colored" });
    } finally {
      setResendLoading(false);
    }
  };

  const handleContinue = async () => {
    if (!code) {
      toast.warn("لطفاً کد تایید جدید را وارد کنید.", { theme: "colored" });
      return;
    }

    try {
      setLoading(true);

      if (!executeRecaptcha) {
        toast.error("مشکل در اعتبارسنجی Recaptcha", { theme: "colored" });
        return;
      }

      let token = await executeRecaptcha("login");

      // اعتبارسنجی کد جدید
      const authResult = await businessService.VerifyPasswordlessLoginCode(
        mobile,
        code,
        token
      );

      if (authResult.accessToken) {
        StoreService.SetData("user", authResult);
        StoreService.SetData("accessKey", authResult.accessToken);
        StoreService.SetData("access-time", Date.now().toString());

        const categories = await getCategories(authResult.accessToken);
        StoreService.SetData("categories", categories);
      }

      const userProfile = await businessService.getUserProfile();
      if (userProfile) {
        StoreService.SetData("user-profile", userProfile);
      }

      toast.success("ورود موفقیت‌آمیز بود!", { theme: "colored" });

      navigate("/");
    } catch (error) {
      toast.error("کد وارد شده صحیح نیست یا منقضی شده است.", {
        theme: "colored",
      });
    } finally {
      setTimeout(() => setLoading(false), 2000);
    }
  };

  const handleGoBack = () => {
    navigate("/mobile-verify");
  };

  return (
    <GoogleReCaptchaProvider reCaptchaKey={AppConsts.reCaptchaKey}>
      <>
        <div
          style={{ height: "100vh", display: "flex", flexDirection: "column" }}
        >
          <div style={{ flex: "0 0 45%", position: "relative", zIndex: 1 }}>
            <SecondPageTopSection
              backgroundImage={BgImage}
              logoImage={logo}
              title={title}
              description={description}
              customStyles={{
                container: { height: "100%" },
                title: { color: "white" },
                description: { color: "white" },
              }}
            />
          </div>

          <div
            style={{
              flex: 1,
              backgroundColor: "#FFFFFF",
              borderRadius: "50px 50px 0 0",
              marginTop: "-50px",
              padding: "20px",
              overflow: "hidden",
              zIndex: 10,
            }}
          >
            <h6 className="fw-bold text-center mb-3">
              لطفاً کد ارسال شده را وارد کنید
            </h6>
            <p className="text-center text-secondary mb-4">
              کد به شماره <strong>{mobile || "09123456789"}</strong> ارسال شده
              است.
            </p>

            <input
              type="tel"
              inputMode="numeric"
              maxLength={6}
              className="form-control text-center fs-4 mb-4"
              placeholder="------"
              style={{
                letterSpacing: "10px",
                padding: "10px",
                backgroundColor: "#F5F5F5",
                borderRadius: "8px",
              }}
              value={code}
              onChange={handleCodeChange}
            />

            <p className="text-center text-secondary mb-3">
              مدت اعتبار کد ارسال شده: {formatTime(timeLeft)}
              <button
                className="btn btn-link text-primary p-0"
                style={{
                  textDecoration: "underline",
                  display: timeLeft !== 0 ? "none" : "block",
                }}
                onClick={handleResendCode}
                disabled={resendLoading}
              >
                {resendLoading ? "در حال ارسال..." : "ارسال مجدد"}
              </button>
            </p>

            <button
              className="btn btn-primary w-100 mb-3"
              style={{
                height: "50px",
                borderRadius: "24px",
                fontWeight: "bold",
                fontSize: "16px",
                backgroundColor: "#695CD5",
              }}
              onClick={handleContinue}
              disabled={loading}
            >
              {loading ? "در حال تایید..." : "تایید"}
            </button>

            <p className="text-center text-secondary">
              <button
                className="btn btn-link text-primary p-0"
                style={{ textDecoration: "underline", cursor: "pointer" }}
                onClick={handleGoBack}
              >
                ویرایش شماره
              </button>
            </p>
          </div>
        </div>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          rtl
          pauseOnHover
          theme="colored"
        />
      </>
    </GoogleReCaptchaProvider>
  );
};

export default MobileOtpPage;
